import {useState} from 'react';
import {signIn,adminAction,publicCatalog} from '../lib/assessmentApi';
import {notifyAssessmentCatalogUpdated} from '../hooks/usePublishedAssessments';
import {newAssessment,duplicateAssessment,assessDraft,sampleAnswers,slugify,replaceOption,addChoice,addQuestion} from '../lib/assessmentBuilder.mjs';
import {buildStandardReport} from '../lib/standardReport.mjs';
import {downloadStandardReport} from '../lib/standardReportPdf';
import './AssessmentBuilder.css';

const copy=o=>JSON.parse(JSON.stringify(o));
function Editor({value,onChange,canEditId}){
  const update=fn=>{const next=copy(value);fn(next);onChange(next);};
  const change=(key,v)=>update(a=>{a[key]=v;});
  const setQuestion=(id,field,v)=>update(a=>{a.questions.find(x=>x.id===id)[field]=v;});
  const setDimension=(id,field,v)=>update(a=>{const d=a.report.dimensions.find(x=>x.questionId===id);if(d)d[field]=v;});
  const scored=id=>value.report.dimensions.some(x=>x.questionId===id);
  const toggleScore=q=>update(a=>{
    if(a.report.dimensions.some(d=>d.questionId===q.id)){
      a.report.dimensions=a.report.dimensions.filter(d=>d.questionId!==q.id);
    }else{
      a.report.dimensions.push({questionId:q.id,label:q.prompt.slice(0,100),
        maxPoints:25,options:Object.fromEntries(q.options.map((option,index)=>[
          option,{points:index===0?0:25,finding:'Describe this result.',recommendation:'Add a recommended improvement.'}
        ]))});
    }
  });
  const changeRule=(id,option,field,v)=>update(a=>{
    const d=a.report.dimensions.find(x=>x.questionId===id);
    if(d){d.options[option]={...d.options[option],[field]:v};}
  });
  const removeQuestion=id=>update(a=>{
    a.questions=a.questions.filter(q=>q.id!==id);
    a.report.dimensions=a.report.dimensions.filter(d=>d.questionId!==id);
  });
  const removeOption=(id,index)=>update(a=>{
    const q=a.questions.find(x=>x.id===id);
    if(q.options.length<=2)return;
    const removed=q.options.splice(index,1)[0];
    const d=a.report.dimensions.find(x=>x.questionId===id);
    if(d)delete d.options[removed];
  });
  return <div className="builder-editor">
    <section className="builder-panel"><h2>Assessment details</h2>
      <div className="builder-fields">
        <label>Permanent assessment ID<input value={value.id} disabled={!canEditId} onChange={e=>change('id',slugify(e.target.value))} title={canEditId?'Choose a unique permanent ID.':'Duplicate to create a different assessment ID.'}/></label>
        <label>Short link code<input value={value.shortCode} onChange={e=>change('shortCode',slugify(e.target.value).slice(0,24))}/></label>
        <label className="builder-span">Title<input value={value.title} maxLength={120} onChange={e=>change('title',e.target.value)}/></label>
        <label className="builder-span">Opening statement<textarea rows={2} value={value.intro} onChange={e=>change('intro',e.target.value)}/></label>
        <label className="builder-span">Description<textarea rows={2} value={value.description} onChange={e=>change('description',e.target.value)}/></label>
      </div>
    </section>
    <section className="builder-panel"><div className="builder-heading"><div><h2>Questions</h2>
      <p>Each published assessment inherits BPA Verify and PDF email delivery.</p></div>
      <div className="builder-actions"><button type="button" onClick={()=>onChange(addQuestion(value,'choice'))}>Add choice</button>
        <button type="button" onClick={()=>onChange(addQuestion(value,'number'))}>Add number</button></div></div>
      {value.questions.map((q,i)=><div className="builder-question" key={q.id}>
        <div className="builder-heading"><strong>{i+1}. {q.id} · {q.type}</strong>
          <button type="button" disabled={value.questions.length===1} onClick={()=>removeQuestion(q.id)}>Remove question</button></div>
        <label>Question text<input value={q.prompt} onChange={e=>setQuestion(q.id,'prompt',e.target.value)}/></label>
        {q.type==='choice'?<>
          <label className="builder-check"><input type="checkbox" checked={scored(q.id)} onChange={()=>toggleScore(q)}/> Include in health score</label>
          <div className="builder-options">{q.options.map((option,index)=><div className="builder-option" key={index}>
            <label>Option {index+1}<input value={option}
              onChange={e=>onChange(replaceOption(value,q.id,index,e.target.value))}/></label>
            <button type="button" disabled={q.options.length<=2} onClick={()=>removeOption(q.id,index)}>Remove</button>
          </div>)}</div>
          <button type="button" onClick={()=>onChange(addChoice(value,q.id))}>Add answer option</button>
          {scored(q.id)&&<div className="builder-scoring">
            <label>Category name<input value={value.report.dimensions.find(d=>d.questionId===q.id).label}
              onChange={e=>setDimension(q.id,'label',e.target.value)}/></label>
            <label>Maximum points<input type="number" min="1" max="100"
              value={value.report.dimensions.find(d=>d.questionId===q.id).maxPoints}
              onChange={e=>setDimension(q.id,'maxPoints',Number(e.target.value))}/></label>
            {q.options.map((option,index)=>{
              const rules=value.report.dimensions.find(d=>d.questionId===q.id).options[option]||{points:0,finding:'',recommendation:''};
              return <div className="builder-rule" key={index}><strong>{option||'Unnamed option'}</strong>
                <label>Points<input type="number" min="0" max="100" value={rules.points}
                  onChange={e=>changeRule(q.id,option,'points',Number(e.target.value))}/></label>
                <label>Finding<textarea rows={2} value={rules.finding}
                  onChange={e=>changeRule(q.id,option,'finding',e.target.value)}/></label>
                <label>Recommended action<textarea rows={2} value={rules.recommendation}
                  onChange={e=>changeRule(q.id,option,'recommendation',e.target.value)}/></label>
              </div>;
            })}
          </div>}
        </>:<div className="builder-fields">
          {['min','max','step'].map(field=><label key={field}>{field}<input type="number" value={q[field]}
            onChange={e=>setQuestion(q.id,field,Number(e.target.value))}/></label>)}
        </div>}
      </div>)}
    </section>
    <section className="builder-panel"><h2>Lead qualification and next steps</h2>
      <p>Lower health scores represent greater operational improvement opportunities. Grade A is the highest-priority lead.</p>
      <div className="builder-fields">
        <label>Grade B maximum health score<input type="number" min="0" max="100"
          value={value.report.qualification.grades[0]}
          onChange={e=>update(a=>{a.report.qualification.grades[0]=Number(e.target.value);})}/></label>
        <label>Grade A maximum health score<input type="number" min="0" max="100"
          value={value.report.qualification.grades[1]}
          onChange={e=>update(a=>{a.report.qualification.grades[1]=Number(e.target.value);})}/></label>
      </div>
      {['A','B','C'].map((grade,index)=><label className="builder-long" key={grade}>Next step for grade {grade}
        <textarea rows={2} value={value.report.nextSteps[index]}
          onChange={e=>update(a=>{a.report.nextSteps[index]=e.target.value;})}/></label>)}
    </section>
  </div>;
}
export default function Admin(){
  const [identity,setIdentity]=useState('');
  const [password,setPassword]=useState('');
  const [token,setToken]=useState('');
  const [workspace,setWorkspace]=useState(null);
  const [selected,setSelected]=useState(null);
  const [dirty,setDirty]=useState(false);
  const [viewOnly,setViewOnly]=useState(false);
  const [busy,setBusy]=useState(false);
  const [notice,setNotice]=useState('');
  const [error,setError]=useState('');
  const refresh=async t=>{const data=await adminAction(t,'load');setWorkspace(data);return data;};
  const login=async e=>{
    e.preventDefault();setError('');setBusy(true);
    try{const next=await signIn(identity,password);await refresh(next);setToken(next);setPassword('');}
    catch(err){setToken('');setError(err.message);}
    finally{setBusy(false);}
  };
  const perform=async(action,payload)=>{
    setError('');setNotice('');setBusy(true);
    try{
      const data=await adminAction(token,action,{expectedRevision:workspace.revision,...payload});
      setWorkspace(data);setDirty(false);
      if(action==='publish'||action==='unpublish'){
        notifyAssessmentCatalogUpdated();
        try{
          const visible=await publicCatalog();
          const listed=visible.some(a=>a.id===payload.id&&a.enabled===true);
          if(action==='publish'&&!listed){
            setNotice('Your assessment was saved as published, but the public catalog does not list it yet. Please check the Gateway catalog endpoint.');
          }else if(action==='unpublish'&&listed){
            setNotice('Unpublish was accepted, but the public catalog still lists the assessment. Refresh the catalog.');
          }else{
            setNotice(action==='publish'?'Published and verified in the public Assessment Hub catalog.':
              'Unpublished and removed from the public Assessment Hub catalog.');
          }
        }catch(err){
          setNotice('Publishing was saved, but the public catalog could not be verified: '+err.message);
        }
      }else setNotice('Draft saved securely.');
      if(action==='save_draft')setSelected(data.drafts.find(x=>x.id===payload.assessment.id));
      if(action==='publish')setViewOnly(true);
    }catch(err){setError(err.message);}
    finally{setBusy(false);}
  };
  const edit=(item,locked=false)=>{setSelected(copy(item));setDirty(false);setViewOnly(locked);setError('');setNotice('');};
  const create=()=>{setViewOnly(false);setSelected(newAssessment('New assessment '+(workspace.drafts.length+workspace.published.length+1)));
    setDirty(true);setNotice('');setError('');};
  const duplicate=item=>{
    const name=window.prompt('New unique assessment ID',item.id+'-copy');
    if(!name)return;
    try{setViewOnly(false);setSelected(duplicateAssessment(item,name));setDirty(true);setError('');}
    catch(err){setError(err.message);}
  };
  const preview=async()=>{
    try{
      const issues=assessDraft(selected);
      if(issues.length)throw new Error(issues.join(' '));
      const answers=sampleAnswers(selected);
      const result=buildStandardReport(selected,answers);
      setNotice('Preview score '+result.score+'/100, lead grade '+result.grade+'. The PDF uses sample answers.');
      await downloadStandardReport(selected,answers,{firstName:'Preview',businessName:'BPA Solutions'});
    }catch(err){setError(err.message);}
  };
  const publish=()=>{
    if(dirty){setError('Save your draft before publishing.');return;}
    const errors=assessDraft(selected);
    if(errors.length){setError(errors.join(' '));return;}
    if(!window.confirm('Publish this assessment to visitors with mandatory OTP and PDF delivery?'))return;
    perform('publish',{id:selected.id});
  };
  const unpublish=id=>{
    if(window.confirm('Unpublish this assessment? Existing links will stop accepting new submissions.'))
      perform('unpublish',{id});
  };
  const signOut=()=>{setToken('');setWorkspace(null);setSelected(null);setIdentity('');setNotice('');};
  if(!token||!workspace)return <main className="builder-shell builder-login">
    <header><a href="/">Assessment Hub</a><strong>BPA Solutions · Administration</strong></header>
    <form className="builder-panel" onSubmit={login}>
      <h1>Administrator sign in</h1>
      <p>Use your verified BPA Email Gateway administrator account. Publishing is protected by server-side permissions.</p>
      <label>Email<input type="email" autoComplete="username" value={identity} onChange={e=>setIdentity(e.target.value)} required/></label>
      <label>Password<input type="password" autoComplete="current-password" value={password} onChange={e=>setPassword(e.target.value)} required/></label>
      <button className="builder-primary" type="submit" disabled={busy}>{busy?'Verifying...':'Sign in securely'}</button>
      {error&&<p role="alert" className="builder-error">{error}</p>}
    </form>
  </main>;
  const drafts=workspace.drafts.filter(x=>x.id!=='timesheet-to-invoice');
  const live=workspace.published.filter(x=>x.id!=='timesheet-to-invoice');
  return <main className="builder-shell">
    <header><a href="/">← Assessment Hub</a><strong>Assessment Builder</strong><button onClick={signOut}>Sign out</button></header>
    <div className="builder-heading builder-intro"><div><h1>Manage assessments</h1>
      <p>Draft, preview and publish without editing the Email Gateway. The existing Timesheet assessment stays protected.</p>
    </div><button className="builder-primary" onClick={create} disabled={busy}>Create assessment</button></div>
    {notice&&<p role="status" className="builder-notice">{notice} <a href="/">View Assessment Hub</a></p>}
    {error&&<p role="alert" className="builder-error">{error}</p>}
    <div className="builder-layout">
      <aside className="builder-panel builder-sidebar"><h2>Drafts</h2>
        {drafts.length===0&&<p>No drafts yet.</p>}
        {drafts.map(a=><button className={selected?.id===a.id?'builder-active':''} key={a.id} onClick={()=>edit(a,false)}>
          {a.title}<small>Draft · {a.id}</small></button>)}
        <h2>Published</h2>
        {live.map(a=><div className="builder-sidebar-item" key={a.id}><button onClick={()=>edit(a,true)}>
          {a.title}<small>{a.enabled?'Live':'Inactive'} · v{a.publishedVersion||1}</small></button>
          <div className="builder-actions"><button onClick={()=>duplicate(a)}>Duplicate</button>
            {a.enabled&&<button onClick={()=>unpublish(a.id)} disabled={busy}>Unpublish</button>}</div></div>)}
        {live.length===0&&<p>No new assessments published yet.</p>}
      </aside>
      <div className="builder-main">
        {selected?<><div className="builder-panel builder-heading"><div><h2>{selected.title}</h2>
          <p>{viewOnly?'Published definition is locked':dirty?'Unsaved edits':'No unsaved edits'} · /a/{selected.id}</p></div>
          <div className="builder-actions"><button type="button" onClick={()=>duplicate(selected)}>Duplicate</button>
            <button type="button" onClick={preview}>Preview PDF</button>
            {!viewOnly&&<><button type="button" disabled={busy} onClick={()=>perform('save_draft',{assessment:selected})}>Save draft</button>
            <button className="builder-primary" type="button" disabled={busy||dirty||!drafts.some(a=>a.id===selected.id)} onClick={publish}>Publish</button></>}
          </div></div>
          {viewOnly?<div className="builder-panel"><h2>Published assessment is protected</h2><p>To change questions or scoring without reinterpreting older reports, duplicate this assessment and publish it under a new ID.</p></div>:<Editor value={selected} canEditId={!drafts.some(a=>a.id===selected.id)&&!live.some(a=>a.id===selected.id)} onChange={a=>{setSelected(a);setDirty(true);}}/>}
        </>:<section className="builder-panel"><h2>Select an assessment or create a new one</h2>
          <p>Published Timesheet-to-Invoice reporting is maintained separately and cannot be changed here.</p>
        </section>}
      </div>
    </div>
  </main>;
}